import type { QuotaCycle } from './quota.js';

// Shared quota cycle-window math. The LLM QuotaEngine, the web-search quota
// gate, and the desktop bootstrap quotaSummary all enforce/report the same
// tenant/group/user caps over the same daily / monthly / lifetime windows, so
// the boundary computation lives here once — previously it was duplicated
// verbatim in two enforcers and could silently drift (e.g. a month-rollover or
// timezone fix applied to only one path). It lives in shared-types (not the
// gateway) so service-api can reuse it without a circular dependency.

export interface UsageWindow {
  from: string;
  to: string;
}

// Lifetime (prepaid-allocation) caps never reset: one fixed window spanning all
// time, so the counter key is stable and consumption accumulates forever.
export const LIFETIME_WINDOW: UsageWindow = {
  from: '1970-01-01T00:00:00.000Z',
  to: '9999-12-31T23:59:59.999Z',
};

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/** UTC [from, to) bounds for the cycle containing `now` (epoch ms). */
export function cycleWindowBounds(cycle: QuotaCycle, now: number): UsageWindow {
  if (cycle === 'lifetime') return LIFETIME_WINDOW;
  const d = new Date(now);
  const year = d.getUTCFullYear();
  const month = d.getUTCMonth();
  const date = d.getUTCDate();
  let from: number;
  let to: number;
  switch (cycle) {
    case 'monthly':
      from = Date.UTC(year, month, 1);
      to = Date.UTC(year, month + 1, 1);
      break;
    case 'weekly': {
      // Fixed calendar week anchored on Monday 00:00 UTC (NOT a rolling 168h
      // window). Date.UTC normalizes a non-positive day-of-month, so the
      // subtraction rolls back into the previous month/year correctly.
      const daysSinceMonday = (d.getUTCDay() + 6) % 7; // Sun=0 → 6
      from = Date.UTC(year, month, date - daysSinceMonday);
      to = from + WEEK_MS; // safe: UTC has no DST, every week is exactly 7×86400s
      break;
    }
    default: // daily
      from = Date.UTC(year, month, date);
      to = Date.UTC(year, month, date + 1);
  }
  return { from: new Date(from).toISOString(), to: new Date(to).toISOString() };
}

/** Stable label for the cycle window containing `now` — used as a counter-map
 *  key suffix so a counter naturally resets when the window rolls over. */
export function cycleWindowLabel(cycle: QuotaCycle, now: number): string {
  if (cycle === 'lifetime') return 'lifetime';
  if (cycle === 'weekly') {
    // Monday's date, `w`-prefixed: derived from the same bounds math so the
    // label can never disagree with the window. Avoids ISO week-numbering-year
    // edge cases (e.g. 2026-12-28 belongs to ISO week 2027-W01).
    return `w${cycleWindowBounds('weekly', now).from.slice(0, 10)}`;
  }
  const d = new Date(now);
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  if (cycle === 'monthly') return `${year}-${month}`;
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** ISO timestamp at which the current cycle resets (the exclusive `to` bound). */
export function cycleResetAt(cycle: QuotaCycle, now: number): string {
  return cycleWindowBounds(cycle, now).to;
}
